// Smart Prompt Guard — warns users BEFORE submitting a prompt that will likely
// trip the image/video safety filter. Lightweight client-side keyword check
// (no network round-trip) tuned to the Gemini/Nano-Banana + fal.ai content
// policies. Keeps false positives low — if nothing matches, zero friction.

const BUCKETS = [
  {
    code: 'violence',
    label: 'Graphic violence',
    hint: 'Avoid wounds, blood, gore, decapitation, torture, mutilation.',
    patterns: [
      /\b(gore|gory|decapitat\w*|dismember\w*|mutilat\w*|disembowel\w*)\b/i,
      /\b(blood\w*|bleeding\s+(wound|body))\b/i,
      /\b(torture|execut\w*|lynch\w*|hang\w+\s+(from|by))\b/i,
      /\b(headless|severed\s+head|severed\s+limbs?)\b/i,
    ],
  },
  {
    code: 'weapons_harm',
    label: 'Weapons aimed at a person',
    hint: 'Rephrase without weapons pointed at people or active shooting scenes.',
    patterns: [
      /\b(gun|pistol|rifle|shotgun|ak-?47|ar-?15)\b.*\b(aim\w*|point\w*|at|shooting|firing|against)\b/i,
      /\b(mass\s+shoot\w*|school\s+shoot\w*|hostage|terror(ist|ism))\b/i,
      /\b(bomb(ing)?|explosiv\w*|molotov|ied|detonat\w*)\b/i,
    ],
  },
  {
    code: 'sexual_minors',
    label: 'Minors in a non-innocent context',
    hint: 'Any sexual, revealing, or suggestive portrayal of minors is blocked. Rephrase.',
    patterns: [
      /\b(child|kid|minor|teen(?!ager\s+hero)|underage|baby|toddler|preteen|loli|shota)\b.*\b(nud\w*|naked|sexy|sexual|lingerie|bikini|revealing|erotic|seductive)\b/i,
      /\b(nud\w*|naked|sexy|sexual|lingerie|bikini|revealing|erotic|seductive)\b.*\b(child|kid|minor|teen(?!ager\s+hero)|underage|baby|toddler|preteen)\b/i,
    ],
  },
  {
    code: 'nudity',
    label: 'Explicit nudity / sexual content',
    hint: 'The image model refuses explicit nudity. Rephrase as artful, SFW, or implied.',
    patterns: [
      /\b(nude|naked)\b.*\b(woman|man|girl|boy|person|body)\b/i,
      /\b(explicit\s+sex|hardcore|porn\w*|penis|vagina|genitalia|erection|orgasm|cumshot|blowjob|anal\s+sex)\b/i,
      /\b(topless|bottomless)\s+(woman|man|girl|boy|teen)\b/i,
    ],
  },
  {
    code: 'hate',
    label: 'Hate / slurs',
    hint: 'Remove slurs, hateful comparisons, or ethnic/religious attacks.',
    patterns: [
      /\b(n[\*\-]word|f[\*\-]ggot|k[\*\-]ke|chink|spic|wetback|towelhead)\b/i,
      /\b(white\s+power|heil\s+hitler|white\s+supremac\w*|final\s+solution)\b/i,
    ],
  },
  {
    code: 'public_figures',
    label: 'Realistic public figures',
    hint: 'Photorealistic depictions of named celebrities / politicians are often blocked. Try a stylized render or a fictional character.',
    patterns: [
      // Only triggers for *photorealistic* intent + a named-person-style capitalized phrase
      /(photorealistic|hyper[-\s]?realistic|real(istic)?\s+photo|lifelike).*\b(Trump|Biden|Obama|Putin|Zelenskyy|Elon\s+Musk|Taylor\s+Swift|Kardashian|Beyonc[eé]|Rihanna|Drake|Kanye)\b/i,
      /\b(Trump|Biden|Obama|Putin|Zelenskyy|Elon\s+Musk|Taylor\s+Swift|Kardashian|Beyonc[eé]|Rihanna|Drake|Kanye)\b.*\b(photorealistic|hyper[-\s]?realistic|real(istic)?\s+photo|lifelike)/i,
    ],
  },
  {
    code: 'self_harm',
    label: 'Self-harm or suicide',
    hint: 'Rephrase without self-injury, suicide, or overdose imagery.',
    patterns: [
      /\b(suicide|self[-\s]harm|cutting\s+(my|her|his)\s+wrist|overdose|hanging\s+(myself|herself|himself))\b/i,
    ],
  },
];

/**
 * Inspect a prompt before submission.
 * @param {string} prompt
 * @returns {{ok: boolean, warnings: Array<{code, label, hint}>}}
 */
export function inspectPrompt(prompt) {
  const text = (prompt || '').trim();
  if (!text) return { ok: true, warnings: [] };
  const hits = [];
  for (const b of BUCKETS) {
    if (b.patterns.some(rx => rx.test(text))) {
      hits.push({ code: b.code, label: b.label, hint: b.hint });
    }
  }
  return { ok: hits.length === 0, warnings: hits };
}
